import { useEffect, useState } from 'react';
import { Page } from '../components/Page.js';

// Owner-side preview of exactly what an inspector with a given set of scopes
// would see. Renders the same read-only data via the owner's endpoints — no grant
// needed, nothing logged (this is the owner looking, not a disclosure).

const SCOPES = [
  { key: 'devices', label: 'Devices' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'reconciliation', label: 'Reconciliation' },
  { key: 'accounts', label: 'Accounts & cards (masked)' },
] as const;

/* eslint-disable @typescript-eslint/no-explicit-any */
export function InspectorPreview() {
  const [scopes, setScopes] = useState<string[]>(['devices', 'notifications', 'reconciliation']);
  const [data, setData] = useState<Record<string, any>>({});

  function toggle(s: string) { setScopes((c) => c.includes(s) ? c.filter((x) => x !== s) : [...c, s]); }

  useEffect(() => {
    (async () => {
      const d: Record<string, any> = {};
      if (scopes.includes('devices')) d.devices = (await fetch('/api/devices').then((r) => r.json()).catch(() => ({}))).devices ?? [];
      if (scopes.includes('notifications')) d.notifications = (await fetch('/api/notifications').then((r) => r.json()).catch(() => ({}))).notifications ?? [];
      if (scopes.includes('reconciliation')) d.reconciliation = await fetch('/api/reconcile').then((r) => r.json()).catch(() => ({}));
      if (scopes.includes('accounts')) { const a = await fetch('/api/accounts').then((r) => r.json()).catch(() => ({})); const c = await fetch('/api/cards').then((r) => r.json()).catch(() => ({})); d.accounts = { accounts: a.accounts ?? [], cards: c.cards ?? [] }; }
      setData(d);
    })();
  }, [scopes]);

  return (
    <Page eyebrow="Evidence & access" title="Inspector preview"
      actions={<span className="pill pill-ok"><span className="dot dot-ok" />preview only · not logged · not shared</span>}>
      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          See exactly what an inspector would see for a chosen set of categories, before you create a grant.
          The credential vault and personal-detail records are never available to an inspector. To create a
          real shareable link, go to <b>Inspector grants</b>.
        </p>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
          {SCOPES.map((s) => (
            <label key={s.key} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13.5 }}>
              <input type="checkbox" checked={scopes.includes(s.key)} onChange={() => toggle(s.key)} />{s.label}
            </label>
          ))}
        </div>
      </section>

      <div style={{ border: '2px dashed var(--border2)', borderRadius: 12, padding: 16, background: 'var(--surface2)' }}>
        <div className="muted" style={{ fontSize: 12, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Inspector would see ↓</div>

        {data.devices && (
          <Block title="Devices" hint={`${data.devices.length}`}>
            {data.devices.map((d: any) => (
              <Row key={d.uuid} badge={d.status?.toUpperCase()} kind="ok" main={<><strong>{d.label}</strong>{(d.make || d.model) ? <span className="muted"> · {[d.make, d.model].filter(Boolean).join(' ')}</span> : null}{d.serial ? <span className="mono muted3"> · {d.serial}</span> : null}</>} />
            ))}
          </Block>
        )}
        {data.notifications && (
          <Block title="Notifications" hint={`${data.notifications.length}`}>
            {data.notifications.map((n: any) => <Row key={n.uuid} badge={n.rule_id ?? 'NOTIFY'} kind="ok" main={<strong>{n.what}</strong>} sub={`told police ${n.notified_date}`} />)}
          </Block>
        )}
        {data.reconciliation?.buckets && (
          <Block title="Reconciliation" hint={`${data.reconciliation.buckets.counts.stale_open} stale`}>
            {data.reconciliation.buckets.on_police_not_yours.map((s: any) => (
              <Row key={s.item_id} badge={s.resolution ? s.resolution.resolution.toUpperCase() : 'STALE'} kind={s.resolution ? 'ok' : 'warn'} main={<span style={{ textTransform: 'capitalize' }}>{s.police}</span>} sub={s.resolution ? `resolved · notified ${s.resolution.notified_date ?? '—'}` : 'police still hold this'} />
            ))}
          </Block>
        )}
        {data.accounts && (
          <Block title="Accounts & cards (masked)" hint="">
            {data.accounts.accounts.map((a: any) => <Row key={a.uuid} badge={a.status?.toUpperCase()} kind="ok" main={<strong>{a.institution}</strong>} sub={a.last4 ? `••${a.last4}` : ''} />)}
            {data.accounts.cards.map((c: any) => <Row key={c.uuid} badge="CARD" kind="ok" main={<strong>{c.brand ?? 'Card'} ••{c.pan_last4}</strong>} />)}
          </Block>
        )}
        {scopes.length === 0 && <div className="muted" style={{ fontSize: 13 }}>Select at least one category above.</div>}
      </div>
    </Page>
  );
}

function Block({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="panel-head"><h3>{title}</h3>{hint && <span className="hint">{hint}</span>}</div>
      <div className="ledger">{children}</div>
    </div>
  );
}
function Row({ badge, kind, main, sub }: { badge?: string; kind: string; main: React.ReactNode; sub?: string }) {
  return (
    <div className="ledger-row">
      {badge && <span className={`badge badge-${kind}`}>{badge}</span>}
      <div className="ledger-main"><div className="ledger-text">{main}</div>{sub && <div className="ledger-time">{sub}</div>}</div>
    </div>
  );
}
